import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { MMEC_PLATFORM_TOUR_SECTIONS } from '../tour/mmecPlatformTour';

export interface TourStep {
  id: string;
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  target: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: 'click' | 'hover' | 'scroll' | 'changeTab' | 'none';
  highlight?: boolean;
  showTooltip?: boolean;
  tooltipContent?: string;
  tooltipContentAr?: string;
  examples?: string[];
  examplesAr?: string[];
  tips?: string[];
  tipsAr?: string[];
  importance?: string;
  importanceAr?: string;
  tabToChange?: string;
}

export interface TourSection {
  id: string;
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  steps: TourStep[];
}

interface TourContextType {
  isTourActive: boolean;
  currentStep: number;
  currentSection: number;
  sections: TourSection[];
  startTour: () => void;
  stopTour: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (stepIndex: number) => void;
  goToSection: (sectionIndex: number) => void;
  highlightElement: (selector: string) => void;
  clearHighlight: () => void;
  getCurrentStep: () => TourStep | null;
  getCurrentSection: () => TourSection | null;
  isStepCompleted: (stepId: string) => boolean;
  markStepCompleted: (stepId: string) => void;
  resetTour: () => void;
  changeTab: (tabName: string) => void;
  executeAction: (action: string, target: string) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

const SIMPLE_TABS = new Set([
  'flow',
  'scanner',
  'gex',
  'volume',
  'elite_pulse',
]);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentSection, setCurrentSection] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const highlightRef = useRef<HTMLDivElement | null>(null);

  const sections = useMemo(() => {
    if (typeof document === 'undefined') return MMEC_PLATFORM_TOUR_SECTIONS;
    const isSimple = Boolean(document.querySelector('.mmec-simple-shell'));
    if (isSimple) {
      return MMEC_PLATFORM_TOUR_SECTIONS.filter((s) => s.id !== 'advanced');
    }
    return MMEC_PLATFORM_TOUR_SECTIONS;
  }, []);

  const startTour = useCallback(() => {
    setIsTourActive(true);
    setCurrentStep(0);
    setCurrentSection(0);
  }, []);

  const stopTour = useCallback(() => {
    setIsTourActive(false);
    setCurrentStep(0);
    setCurrentSection(0);
    if (highlightRef.current?.style) {
      highlightRef.current.style.display = 'none';
      highlightRef.current.style.boxShadow = 'none';
    }
    window.dispatchEvent(new CustomEvent('mmec-platform-tour-ended'));
  }, []);

  const nextStep = useCallback(() => {
    const currentSectionData = sections[currentSection];
    if (currentStep < currentSectionData.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      setCurrentStep(0);
    } else {
      stopTour();
    }
  }, [currentSection, currentStep, sections, stopTour]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      const previousSection = sections[currentSection - 1];
      setCurrentStep(previousSection.steps.length - 1);
    }
  }, [currentStep, currentSection, sections]);

  const goToStep = useCallback((stepIndex: number) => {
    setCurrentStep(stepIndex);
  }, []);

  const goToSection = useCallback((sectionIndex: number) => {
    setCurrentSection(sectionIndex);
    setCurrentStep(0);
  }, []);

  const highlightElement = useCallback((selector: string) => {
    const element = document.querySelector(selector);
    if (element && highlightRef.current?.style) {
      const rect = element.getBoundingClientRect();
      const pad = 6;
      highlightRef.current.style.display = 'block';
      highlightRef.current.style.position = 'fixed';
      highlightRef.current.style.top = `${rect.top - pad}px`;
      highlightRef.current.style.left = `${rect.left - pad}px`;
      highlightRef.current.style.width = `${rect.width + pad * 2}px`;
      highlightRef.current.style.height = `${rect.height + pad * 2}px`;
      highlightRef.current.style.border = '3px solid var(--acc, #45a29e)';
      highlightRef.current.style.backgroundColor = 'rgba(69, 162, 158, 0.12)';
      highlightRef.current.style.zIndex = '9999';
      highlightRef.current.style.pointerEvents = 'none';
      highlightRef.current.style.borderRadius = '8px';
      highlightRef.current.style.transition = 'all 0.3s ease';
      highlightRef.current.style.boxShadow = '0 0 0 9999px rgba(0, 0, 0, 0.45)';
    }
  }, []);

  const clearHighlight = useCallback(() => {
    if (highlightRef.current?.style) {
      highlightRef.current.style.display = 'none';
      highlightRef.current.style.boxShadow = 'none';
    }
  }, []);

  const getCurrentStep = useCallback(() => {
    const currentSectionData = sections[currentSection];
    return currentSectionData?.steps[currentStep] || null;
  }, [currentSection, currentStep, sections]);

  const getCurrentSection = useCallback(() => {
    return sections[currentSection] || null;
  }, [currentSection, sections]);

  const isStepCompleted = useCallback(
    (stepId: string) => completedSteps.has(stepId),
    [completedSteps],
  );

  const markStepCompleted = useCallback((stepId: string) => {
    setCompletedSteps((prev) => new Set(Array.from(prev).concat([stepId])));
  }, []);

  const resetTour = useCallback(() => {
    setCompletedSteps(new Set());
    setCurrentStep(0);
    setCurrentSection(0);
  }, []);

  const changeTab = useCallback((tabName: string) => {
    const tab = (tabName || '').trim();
    if (!tab) return;
    if (SIMPLE_TABS.has(tab)) {
      window.dispatchEvent(
        new CustomEvent('mmec-simple-navigate-tab', { detail: { tab } }),
      );
      return;
    }
    window.dispatchEvent(
      new CustomEvent('mmec-tour-select-component', { detail: { id: tab } }),
    );
    document.dispatchEvent(
      new CustomEvent('tourChangeTab', { detail: { tabName: tab } }),
    );
  }, []);

  const executeAction = useCallback(
    (action: string, target: string) => {
      switch (action) {
        case 'click': {
          const clickElement = document.querySelector(target);
          if (clickElement) (clickElement as HTMLElement).click();
          break;
        }
        case 'hover': {
          const hoverElement = document.querySelector(target);
          if (hoverElement) {
            hoverElement.dispatchEvent(
              new MouseEvent('mouseenter', { bubbles: true }),
            );
          }
          break;
        }
        case 'scroll': {
          const scrollElement = document.querySelector(target);
          if (scrollElement) {
            scrollElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          break;
        }
        case 'changeTab':
          changeTab(target);
          break;
        default:
          break;
      }
    },
    [changeTab],
  );

  return (
    <TourContext.Provider
      value={{
        isTourActive,
        currentStep,
        currentSection,
        sections,
        startTour,
        stopTour,
        nextStep,
        previousStep,
        goToStep,
        goToSection,
        highlightElement,
        clearHighlight,
        getCurrentStep,
        getCurrentSection,
        isStepCompleted,
        markStepCompleted,
        resetTour,
        changeTab,
        executeAction,
      }}
    >
      {children}
      {isTourActive && (
        <div
          ref={highlightRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            display: 'none',
            border: '3px solid var(--acc, #45a29e)',
            backgroundColor: 'rgba(69, 162, 158, 0.12)',
            zIndex: 9999,
            pointerEvents: 'none',
            borderRadius: '8px',
            transition: 'all 0.3s ease',
          }}
        />
      )}
    </TourContext.Provider>
  );
};
